import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Calculator, Download, DollarSign, Percent } from "lucide-react";

export default function TaxCalculator() {
  const [income, setIncome] = useState("");
  const [deductions, setDeductions] = useState("");
  const [filingStatus, setFilingStatus] = useState("single");
  const [result, setResult] = useState(null);

  const calculate = () => {
    const grossIncome = parseFloat(income) || 0;
    const totalDeductions = parseFloat(deductions) || 0;
    const taxableIncome = Math.max(0, grossIncome - totalDeductions);
    
    // Simplified tax brackets for 2024
    let tax = 0;
    if (filingStatus === "single") {
      if (taxableIncome <= 11600) tax = taxableIncome * 0.10;
      else if (taxableIncome <= 47150) tax = 1160 + (taxableIncome - 11600) * 0.12;
      else if (taxableIncome <= 100525) tax = 5426 + (taxableIncome - 47150) * 0.22;
      else if (taxableIncome <= 191950) tax = 17168 + (taxableIncome - 100525) * 0.24;
      else tax = 39110 + (taxableIncome - 191950) * 0.32;
    } else {
      if (taxableIncome <= 23200) tax = taxableIncome * 0.10;
      else if (taxableIncome <= 94300) tax = 2320 + (taxableIncome - 23200) * 0.12;
      else if (taxableIncome <= 201050) tax = 10852 + (taxableIncome - 94300) * 0.22;
      else if (taxableIncome <= 383900) tax = 34336 + (taxableIncome - 201050) * 0.24;
      else tax = 78220 + (taxableIncome - 383900) * 0.32;
    }
    
    // Self-employment tax (15.3% on 92.35% of income)
    const selfEmploymentTax = grossIncome * 0.9235 * 0.153;
    const totalTax = tax + selfEmploymentTax;
    const effectiveRate = grossIncome > 0 ? (totalTax / grossIncome) * 100 : 0;
    const takeHome = grossIncome - totalTax;
    const potentialSavings = totalDeductions * 0.22; // Assuming 22% bracket

    setResult({ taxableIncome, incomeTax: tax, selfEmploymentTax, totalTax, effectiveRate, takeHome, potentialSavings });
  };

  return (
    <div className="min-h-screen" style={{ background: '#fffdf5' }}>
      <nav className="border-b border-[#e5e5e5] bg-white/80 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-2 text-[#666] hover:text-[#121212]">
            <ArrowLeft className="w-4 h-4" /> Back to FinAI
          </Link>
        </div>
      </nav>
      
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-[#ffcc00]/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Calculator className="w-8 h-8 text-[#121212]" />
          </div>
          <h1 className="text-4xl font-bold text-[#121212] mb-4">Tax Savings Calculator</h1>
          <p className="text-lg text-[#666]">Estimate your tax liability and potential savings as a self-employed professional.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <Card className="border-2 border-[#e5e5e5]">
            <CardHeader><CardTitle>Your Information</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Annual Gross Income</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                  <Input type="number" value={income} onChange={e => setIncome(e.target.value)} placeholder="100000" className="pl-9" />
                </div>
              </div>
              <div>
                <Label>Business Deductions</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                  <Input type="number" value={deductions} onChange={e => setDeductions(e.target.value)} placeholder="15000" className="pl-9" />
                </div>
              </div>
              <div>
                <Label>Filing Status</Label>
                <select value={filingStatus} onChange={e => setFilingStatus(e.target.value)} className="w-full mt-1 h-10 px-3 border border-[#e5e5e5] rounded-lg">
                  <option value="single">Single</option>
                  <option value="married">Married Filing Jointly</option>
                </select>
              </div>
              <Button onClick={calculate} className="w-full h-12 rounded-full bg-[#121212] hover:bg-[#2a2a2a] text-white font-medium">
                Calculate Tax Savings
              </Button>
            </CardContent>
          </Card>

          <Card className={`border-2 ${result ? 'border-[#ffcc00]' : 'border-[#e5e5e5]'}`}>
            <CardHeader><CardTitle>Your Results</CardTitle></CardHeader>
            <CardContent>
              {result ? (
                <div className="space-y-4">
                  <div className="p-4 bg-[#faf8f0] rounded-xl">
                    <p className="text-sm text-[#666]">Estimated Total Tax</p>
                    <p className="text-3xl font-bold text-[#121212]">${result.totalTax.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                    <p className="text-sm text-[#666]">{result.effectiveRate.toFixed(1)}% effective rate</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-[#faf8f0] rounded-lg">
                      <p className="text-xs text-[#666]">Income Tax</p>
                      <p className="text-lg font-semibold">${result.incomeTax.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                    </div>
                    <div className="p-3 bg-[#faf8f0] rounded-lg">
                      <p className="text-xs text-[#666]">Self-Employment Tax</p>
                      <p className="text-lg font-semibold">${result.selfEmploymentTax.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                    </div>
                  </div>
                  <div className="p-4 bg-[#ffcc00]/20 rounded-xl border border-[#ffcc00]">
                    <p className="text-sm text-[#666]">Potential Tax Savings</p>
                    <p className="text-2xl font-bold text-[#121212]">${result.potentialSavings.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                    <p className="text-xs text-[#666]">With FinAI's tax optimization strategies</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-xl">
                    <p className="text-sm text-[#666]">Estimated Take Home</p>
                    <p className="text-2xl font-bold text-green-600">${result.takeHome.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-[#666]">
                  <Percent className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Enter your information to see your tax estimate</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="mt-12 text-center">
          <p className="text-[#666] mb-4">Want to maximize your tax savings?</p>
          <Link to="/register">
            <Button className="h-12 px-8 rounded-full bg-[#ffcc00] text-[#121212] font-bold hover:bg-[#ffe566]">
              Get Started with FinAI
            </Button>
          </Link>
        </div>
      </main>
    </div>
  );
}
